import { useEffect, useState } from "react";
import Pagination from "../../common/Pagination";
import { MdDeleteForever } from "react-icons/md";
import { BiSolidEditAlt } from "react-icons/bi";
import Loader from "../../common/Loader";
import axiosInstance from "../../api/axiosInstance.js";
import { notify } from "../../common/Toast/index.js";
import Model from "../../common/Model/index.js";
import Select from "react-select";
import { FaMinus, FaPlus, FaStarOfLife } from "react-icons/fa";
import { getUserMenuPermission } from "../../common/Helper/index.js";

const MenuTable = () => {
  const permission = getUserMenuPermission(12);
  const [isMenuData, setIsMenuData] = useState([]);
  const [users, setUsers] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [errors, setErrors] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [formData, setFormData] = useState({
    menuId: [],
  });
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    fetchMenuData();
    fetchUsers();
    fetchMenus();
  }, [currentPage]);

  const fetchMenus = async () => {
    try {
      const { data } = await axiosInstance.get(
        "UserMenuPermission/GetAllMenus"
      );
      setMenus(data);
    } catch (err) {
      console.error("Error fetching menus:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axiosInstance.get("User/GetAllUser");
      setUsers(data);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchMenuData = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.post(
        `UserMenuPermission/getAllWithMenuName?pageNumber=${currentPage}&pageSize=${pageSize}`,
        {
          pageNumber: currentPage,
          pageSize,
        }
      );
      setIsMenuData(data.data);
      setTotalRecords(data.totalRecords);
      setTotalPages(Math.ceil(data.totalRecords / pageSize));
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMenuPermision = (id) => {
    setSelectedMenuId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(
        `UserMenuPermission/DeleteUser/${selectedMenuId}`
      );
      fetchMenuData();
      notify("Menu deleted successfully!", "error");
    } catch (err) {
      notify("Failed to delete Menu!", "error");
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedMenuId(null);
    }
  };

  const handleEditMenuPermision = async (id) => {
    try {
      const { data } = await axiosInstance.get(
        `UserMenuPermission/getByUserId/${id}`
      );
      if (data.length > 0) {
        setFormData({
          userId: data[0].userId,
          menuId: data.map((item) => ({
            menuId: item.menuId.toString(),
            insert: item.isInsert,
            update: item.isUpdate,
            delete: item.isDelete,
            view: item.isView,
          })),
        });

        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (err) {
      console.error("Error fetching data by ID:", err);
    }
  };

  const handleAddMenuPermision = () => {
    if (!permission.isInsert) {
      notify("You don't have permission to add menu.", "error");
      return;
    }
    setFormData({
      menuId: [
        {
          userId: "",
          menuId: "",
          insert: false,
          update: false,
          delete: false,
          view: false,
        },
      ],
    });
    setIsEditing(false);
    setIsModelOpen(true);
  };
  const handleAddRow = () => {
    setFormData({
      ...formData,
      menuId: [
        ...formData.menuId,
        {
          userId: "",
          menuId: "",
          insert: false,
          update: false,
          delete: false,
          view: false,
        },
      ],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && !permission.isInsert) return;
    if (isEditing && !permission.isUpdate) return;
    if (!formData.userId) {
      notify("Please select user", "error");
      return;
    }

    const payload = formData.menuId.map((menu) => ({
      UserId: Number(formData.userId),
      MenuId: Number(menu.menuId),
      IsInsert: menu.insert,
      IsUpdate: menu.update,
      IsDelete: menu.delete,
      IsView: menu.view,
      Created_By: userId,
      Modified_By: userId,
    }));

    try {
      await axiosInstance.post(`UserMenuPermission/saveOrUpdate`, payload);

      notify(
        isEditing ? "Menu Permission Updated" : "Menu Permission Saved",
        "success"
      );
      fetchMenuData();
      setIsModelOpen(false);
    } catch (err) {
      console.error("Error Saving/Updating:", err);
      notify("Something went wrong!", "error");
    }
  };

  const handleDeleteRow = async (menu, index) => {
    try {
      const updated = [...formData.menuId];

      if (menu.menuId) {
        await axiosInstance.delete(
          `UserMenuPermission/delete/${formData.userId}/${menu.menuId}`
        );
        notify("Row Deleted Successfully", "success");
      }

      updated.splice(index, 1);
      setFormData({ ...formData, menuId: updated });
    } catch (err) {
      notify("Failed to delete row!", "error");
    }
  };

  const Options = [
    ...users.map((per) => ({
      value: per.sale_Person_Id?.toString() || "",
      label: per.Sale_Person_Name || "Unnamed User",
    })),
  ];

  const menuOptions = menus.map((menu) => ({
    value: String(menu.menuIDs),
    label: menu.menu_Name || "Unnamed Menu",
    isDisabled: formData.menuId.some(
      (item) => String(item.menuId) === String(menu.menuIDs)
    ),
  }));

  return (
    <>
      <div >
        <div className="rounded-3xl bg-transparent px-2 pt-2 pb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Access Control Matrix</h2>
              <p className="text-sm text-gray-500 font-medium">Configure granular menu-level permissions for each user</p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
              {permission.isInsert && (
                <button
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary hover:bg-primary/95 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-primary/20 transition-all active:scale-95"
                  onClick={() => handleAddMenuPermision()}
                >
                  <span>+</span> Add Menu Permission
                </button>
              )}
            </div>
          </div>

          <div className="premium-card overflow-hidden border-none shadow-xl shadow-gray-200/50">
            {loading ? (
              <div className="p-20 flex justify-center"><Loader /></div>
            ) : permission.isView ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-[#f9fafc] text-[#405189] uppercase tracking-wider text-[11px] font-bold border-b border-gray-100">
                    <tr>
                      <th className="px-6 py-4">User Name</th>
                      <th className="px-6 py-4">Configured Menus</th>
                      <th className="px-6 py-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {isMenuData?.length > 0 ? (
                      isMenuData?.map((menu, idx) => (
                        <tr key={idx} className="hover:bg-blue-50/30 transition-colors group cursor-pointer">
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="font-bold text-gray-800">
                                {users?.find(
                                  (user) =>
                                    Number(user.sale_Person_Id) === Number(menu.userId)
                                )?.Sale_Person_Name || "Unknown"}
                              </span>
                              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">User ID: {menu.userId}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1.5 max-w-md">
                              {menu.menuNames?.split(',').map((name, i) => (
                                <span key={i} className="px-2 py-0.5 bg-primary/5 text-primary rounded-md text-[10px] font-bold border border-primary/10">
                                  {name.trim()}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              {permission.isUpdate && (
                                <button
                                  className="p-2 bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
                                  onClick={(e) => { e.stopPropagation(); handleEditMenuPermision(menu.userId); }}
                                  title="Edit Permissions"
                                >
                                  <BiSolidEditAlt className="w-4 h-4" />
                                </button>
                              )}
                              {permission.isDelete && (
                                <button
                                  className="p-2 bg-gray-50 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteMenuPermision(menu.userId); }}
                                  title="Delete Permissions"
                                >
                                  <MdDeleteForever className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center py-20 text-gray-400 font-medium italic">
                          No access control data found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-red-500 text-lg py-20 font-bold bg-white">
                Access Denied: You don't have permission to view menu tables.
              </div>
            )}

            <div className="bg-[#f9fafc] px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-[13px] text-gray-500 font-medium">
                Showing <span className="font-bold text-gray-800">
                  {isMenuData?.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, totalRecords)}
                </span> of{" "}
                <span className="font-bold text-gray-800">{totalRecords}</span> records
              </p>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Modify Permissions" : "Grant NEW Permissions"}
      >
        <form onSubmit={handleSubmit} className="p-2 space-y-6">
          <div className="bg-gray-50/50 p-4 rounded-2xl border border-gray-100">
            <label className="flex items-center text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Select Target User
              <FaStarOfLife className="text-red-500 ml-1 w-1.5 h-1.5" />
            </label>
            {isEditing ? (
              <div className="w-full bg-white border border-gray-200 px-4 py-2.5 rounded-xl text-gray-700 font-bold shadow-sm">
                {users.find(
                  (user) =>
                    Number(user.sale_Person_Id) === Number(formData.userId)
                )?.Sale_Person_Name || "Unknown"}
              </div>
            ) : (
              <Select
                options={Options}
                value={
                  formData.userId
                    ? Options.find(
                      (option) =>
                        String(option.value) === String(formData.userId)
                    ) || null
                    : null
                }
                isClearable
                isSearchable
                onChange={(selectedOption) =>
                  setFormData((prev) => ({
                    ...prev,
                    userId: selectedOption ? selectedOption.value : "",
                  }))
                }
                menuPortalTarget={document.body}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: '0.75rem',
                    padding: '2px',
                    borderColor: errors.userId ? "#ef4444" : "#f3f4f6",
                    backgroundColor: '#ffffff',
                    boxShadow: "none",
                    "&:hover": { borderColor: "#405189" },
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999999 }),
                }}
              />
            )}
            {errors.userId && (
              <p className="text-red-500 text-[11px] font-bold mt-1.5 ml-1 uppercase tracking-tight">{errors.userId}</p>
            )}
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-100 shadow-sm bg-white">
            <table className="w-full text-xs">
              <thead className="bg-[#f9fafc] text-gray-500 font-bold uppercase tracking-tight">
                <tr>
                  <th className="px-4 py-3 text-left">Module / Menu</th>
                  <th className="px-2 py-3 text-center w-16">Ins</th>
                  <th className="px-2 py-3 text-center w-16">Upd</th>
                  <th className="px-2 py-3 text-center w-16">Del</th>
                  <th className="px-2 py-3 text-center w-16">Viw</th>
                  <th className="px-4 py-3 text-center w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {formData.menuId?.map((menu, index) => (
                  <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <Select
                        options={menuOptions}
                        value={menuOptions.find(
                          (opt) => String(opt.value) === String(menu.menuId)
                        )}
                        isClearable
                        isSearchable
                        placeholder="Select Menu..."
                        onChange={(selected) => {
                          const updated = [...formData.menuId];
                          updated[index].menuId = selected ? selected.value : "";
                          setFormData({ ...formData, menuId: updated });
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            borderRadius: '0.6rem',
                            minHeight: '36px',
                            borderColor: '#f3f4f6',
                            fontSize: '11px',
                            boxShadow: "none",
                            "&:hover": { borderColor: "#405189" },
                          }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999999 }),
                        }}
                      />
                    </td>
                    {["insert", "update", "delete", "view"].map((perm) => (
                      <td className="px-2 py-3 text-center" key={perm}>
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-primary border-gray-200 rounded focus:ring-primary cursor-pointer transition-transform hover:scale-110"
                          checked={menu[perm]}
                          onChange={() => {
                            const updated = [...formData.menuId];
                            updated[index][perm] = !menu[perm];
                            setFormData({ ...formData, menuId: updated });
                          }}
                        />
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-1.5">
                        <button
                          type="button"
                          className="p-2 text-red-400 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors border border-transparent hover:border-red-100"
                          onClick={() => handleDeleteRow(menu, index)}
                          title="Remove Row"
                        >
                          <FaMinus className="w-3 h-3" />
                        </button>
                        {index === formData.menuId.length - 1 && (
                          <button
                            type="button"
                            onClick={() => handleAddRow()}
                            className="p-2 text-primary hover:bg-primary/5 rounded-lg transition-colors border border-transparent hover:border-primary/20"
                            title="Add Row"
                          >
                            <FaPlus className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              className="px-6 py-2.5 rounded-xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              onClick={() => setIsModelOpen(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-8 py-2.5 rounded-xl font-bold text-white bg-primary hover:bg-primary/95 shadow-lg shadow-primary/20 transition-all active:scale-95"
            >
              {isEditing ? "Commit Changes" : "Save Matrix"}
            </button>
          </div>
        </form>
      </Model>

      <Model
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <MdDeleteForever className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Confirm Removal</h3>
          <p className="text-gray-500 mb-8 max-w-[300px] mx-auto">
            You are about to remove all configured menu permissions for this user. This action cannot be undone.
          </p>
          <div className="flex justify-center gap-3">
            <button
              className="px-6 py-2.5 rounded-xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="px-6 py-2.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200 transition-all active:scale-95"
              onClick={handleConfirmDelete}
            >
              Confirm Delete
            </button>
          </div>
        </div>
      </Model>
    </>
  );
};

export default MenuTable;
